import { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "@/core/redux/store";
import { RootState } from "@/core/redux/store";
import { useTranslation } from "react-i18next";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import {
  File,
  Folder,
  Folders,
  ChevronDown,
  Loader2,
  Search,
  ChevronRight,
} from "lucide-react";
import { Input } from "@/shared/components/ui/input";
import { debounce } from "lodash";
import {
  getRootFoldersThunk,
  getFolderContentsThunk,
} from "@/core/redux/thunks/specification-folders-thunk";
import {
  ItemType,
  IFolderItem,
  ISpecificationItem,
  IFolderContent,
} from "@/shared/services/interface/types/ISpecificationFoldersTypes";
import { cn } from "@/lib/utils";

interface ISelectProjectDropdownProps {
  value: string;
  onValueChange: (value: string, itemType?: "folder" | "specification" | "all") => void;
  align?: "start" | "center" | "end";
  className?: string;
  labelClassName?: string;
}

export function SelectProjectDropdown({
  value,
  onValueChange,
  align = "end",
  className = "",
  labelClassName = "",
}: ISelectProjectDropdownProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const company = useSelector((state: RootState) => state.user.me?.company);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const expandedItemsRef = useRef<Set<string>>(new Set());

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  // Local state for API data
  const [items, setItems] = useState<ItemType[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [pagination, setPagination] = useState({
    offset: 0,
    limit: 10,
    totalCount: 0,
  });
  const [searchValue, setSearchValue] = useState("");
  const [nestedFolderContents, setNestedFolderContents] = useState<
    Record<string, IFolderContent>
  >({});
  const [error, setError] = useState<string | null>(null);

  // Load root folders
  const loadRootFolders = useCallback(
    async (
      offset: number = 0,
      search: string = "",
      append: boolean = false
    ) => {
      if (!company?.id) return;

      try {
        if (offset === 0) {
          setLoading(true);
        } else {
          setLoadingMore(true);
        }
        setError(null);

        const response = await dispatch(
          getRootFoldersThunk({
            companyId: company.id,
            sortBy: "createdAt",
            order: "DESC",
            offset,
            limit: 10,
            filter: "folder",
            search,
            hideLoadingEffect: true,
          })
        ).unwrap();

        // Response structure: { success, items, total, ... }
        const responseData = response as any;
        if (responseData.success) {
          if (append) {
            setItems((prev) => [...prev, ...responseData.items]);
          } else {
            setItems(responseData.items);
          }
          setPagination({
            offset: responseData.items.length,
            limit: 10,
            totalCount: responseData.total,
          });
        }
      } catch (err: any) {
        setError(err?.message || t("an-error-occurred"));
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [company?.id, dispatch]
  );

  // Load nested folder contents
  const loadFolderContents = useCallback(
    async (folderId: string, search: string = "") => {
      if (!company?.id) return;

      try {
        setNestedFolderContents((prev) => ({
          ...prev,
          [folderId]: {
            items: prev[folderId]?.items || [],
            pagination: prev[folderId]?.pagination || {
              offset: 0,
              limit: 100,
              totalCount: 0,
            },
            isLoading: true,
            error: null,
          },
        }));

        const response = await dispatch(
          getFolderContentsThunk({
            folderId,
            companyId: company.id,
            limit: 100,
            hideLoadingEffect: true,
            search: search,
          })
        ).unwrap();

        // Response structure: { folderId, response: { success, items, total, ... } }
        const responseData = response as any;
        if (responseData.response?.success) {
          setNestedFolderContents((prev) => ({
            ...prev,
            [folderId]: {
              items: responseData.response.items || [],
              pagination: {
                offset: 0,
                limit: 100,
                totalCount: responseData.response.total || 0,
              },
              isLoading: false,
              error: null,
            },
          }));
        } else {
          setNestedFolderContents((prev) => ({
            ...prev,
            [folderId]: {
              items: prev[folderId]?.items || [],
              pagination: prev[folderId]?.pagination || {
                offset: 0,
                limit: 100,
                totalCount: 0,
              },
              isLoading: false,
              error: t("content-could-not-be-loaded"),
            },
          }));
        }
      } catch (err: any) {
        setNestedFolderContents((prev) => ({
          ...prev,
          [folderId]: {
            items: prev[folderId]?.items || [],
            pagination: prev[folderId]?.pagination || {
              offset: 0,
              limit: 100,
              totalCount: 0,
            },
            isLoading: false,
            error: err?.message || t("an-error-occurred"),
          },
        }));
      }
    },
    [company?.id, dispatch, t]
  );

  // Sync expandedItemsRef with expandedItems state
  useEffect(() => {
    expandedItemsRef.current = expandedItems;
  }, [expandedItems]);

  // Reset expands and reload nested folder contents when search changes
  useEffect(() => {
    // Reset all expands when search changes
    setExpandedItems(new Set());
    setNestedFolderContents({});
  }, [searchValue]);

  // Debounced search
  const searchWithDebounce = useCallback(
    debounce(async (search: string) => {
      if (!company?.id) return;
      try {
        await loadRootFolders(0, search, false);
      } catch (error: any) {
        console.error("Error searching folders", error);
        setError(error?.message || t("an-error-occurred"));
      }
    }, 500),
    [company?.id, loadRootFolders, t]
  );

  // Handle search change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.trimStart();
    setSearchValue(value);
    searchWithDebounce(value);
  };

  // Handle dropdown open/close
  const handleOpenChange = (open: boolean) => {
    setDropdownOpen(open);
    if (open && items.length === 0 && !loading) {
      loadRootFolders(0, "", false);
    }
  };

  // Infinite scroll handler
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer || !dropdownOpen) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
      const isNearBottom = scrollTop + clientHeight >= scrollHeight - 50;
      const hasMore = pagination.totalCount > items.length;

      if (isNearBottom && hasMore && !loadingMore && !loading) {
        loadRootFolders(items.length, searchValue, true);
      }
    };

    scrollContainer.addEventListener("scroll", handleScroll);
    return () => scrollContainer.removeEventListener("scroll", handleScroll);
  }, [
    dropdownOpen,
    items.length,
    pagination.totalCount,
    loadingMore,
    loading,
    searchValue,
    loadRootFolders,
  ]);

  // Load nested folder when expanded
  const toggleExpand = async (itemId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
        // Load folder contents if not already loaded
        if (!nestedFolderContents[itemId]) {
          loadFolderContents(itemId, searchValue);
        }
      }
      return newSet;
    });
  };

  // Calculate folder and specification counts
  const getCounts = () => {
    const folderCount = items.filter(
      (item) => item.itemType === "folder"
    ).length;
    const specCount = items.filter(
      (item) => item.itemType === "specification"
    ).length;
    return { folderCount, specCount };
  };

  // Get selected item (search in items and nested folder contents)
  const getSelectedItem = (): ItemType | null => {
    if (value === "all") return null;

    // Search in root items
    const rootItem = items.find((item) => item.id === value);
    if (rootItem) return rootItem;

    // Search in nested folder contents
    for (const folderId in nestedFolderContents) {
      const folderContent = nestedFolderContents[folderId];
      const nestedItem = folderContent.items?.find((item) => item.id === value);
      if (nestedItem) return nestedItem;
    }

    return null;
  };

  const getDisplayText = () => {
    if (value === "all") return t("all-projects");
    const selectedItem = getSelectedItem();
    if (selectedItem) return selectedItem.name;
    return t("all-projects");
  };

  const getSelectedIcon = () => {
    if (value === "all")
      return <Folders className="h-[16px] w-[16px] text-[#09090b] shrink-0" />;
    const selectedItem = getSelectedItem();
    if (selectedItem) {
      if (selectedItem.itemType === "folder") {
        return <Folder className="h-[16px] w-[16px] text-[#09090b] shrink-0" />;
      } else {
        return (
          <File className="h-[16px] ml-[1px] w-[16px] text-[#09090b] shrink-0" />
        );
      }
    }
    return <File className="h-[16px] w-[16px] text-[#09090b] shrink-0" />;
  };

  const getSelectedLabel = () => {
    if (value === "all") return null;
    const selectedItem = getSelectedItem();
    if (selectedItem) {
      if (selectedItem.itemType === "folder") {
        return t("selected-project");
      } else {
        return t("selected-specification");
      }
    }
    return null;
  };

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      searchWithDebounce.cancel();
    };
  }, [searchWithDebounce]);

  const { folderCount, specCount } = getCounts();
  const isAllSelected = value === "all";

  // Render nested items for a folder
  const renderNestedItems = (folderId: string, level: number = 1) => {
    const folderContent = nestedFolderContents[folderId];
    if (!expandedItems.has(folderId)) return null;

    // Show loading state
    if (!folderContent || folderContent.isLoading) {
      return (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="h-4 w-4 animate-spin text-[#71717a]" />
        </div>
      );
    }

    // Show error state
    if (folderContent.error) {
      return (
        <div className="flex items-center justify-center py-4">
          <p className="text-xs text-red-500">{folderContent.error}</p>
        </div>
      );
    }

    const nestedItems = folderContent.items || [];
    const paddingLeft = level === 1 ? 35 : 58;

    // Show empty state
    if (nestedItems.length === 0) {
      return (
        <div className="flex items-center justify-center py-4">
          <p className="text-xs text-[#71717a]">{t("content-not-found")}</p>
        </div>
      );
    }

    return (
      <div className="relative">
        <div
          className="absolute inset-y-[5px] w-[1px] bg-[#E4E4E7]"
          style={{ left: level === 1 ? 22 : 51 }}
        ></div>
        {nestedItems.map((item) => {
          if (item.itemType === "folder") {
            const folderItem = item as IFolderItem;
            const isNestedExpanded = expandedItems.has(folderItem.id);
            const nestedFolderContent = nestedFolderContents[folderItem.id];
            const isLoadingNested = nestedFolderContent?.isLoading || false;

            const isNestedSelected = value === folderItem.id;

            return (
              <div key={folderItem.id}>
                <div
                  className={cn(
                    "flex items-center h-[45px]  relative rounded-[4px]",
                    {
                      "bg-[#F4F4F5]": isNestedSelected,
                      "hover:bg-gray-50": !isNestedSelected,
                    }
                  )}
                  style={{
                    marginLeft: `${paddingLeft}px`,
                    width: `calc(100% - ${paddingLeft}px)`,
                  }}
                >
                  <button
                    onClick={() => {
                      onValueChange(folderItem.id, "folder");
                      setDropdownOpen(false);
                    }}
                    className={
                      "flex py-[8px] relative  h-full items-center gap-2 flex-1 min-w-0 px-2 cursor-pointer"
                    }
                  >
                    {isNestedSelected && (
                      <div className="absolute left-0 top-[13px] w-[2px] h-[20px] bg-[#09090b] rounded-r-full" />
                    )}
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <Folder className="h-4 w-4 text-[#09090b] shrink-0" />
                      <div className="flex flex-col items-start justify-center flex-1 min-w-0">
                        <p className="text-[12px] font-regular text-[#09090b] leading-4 truncate w-full text-left">
                          {folderItem.name}
                        </p>
                        {(folderItem as any).folderCount !== undefined ||
                        (folderItem as any).totalSpecificationCount !==
                          undefined ? (
                          <p className="text-[10px] text-left font-book text-[#71717a] leading-4 truncate w-full">
                            {(folderItem as any).folderCount || 0}{" "}
                            {t("project")},{" "}
                            {(folderItem as any).totalSpecificationCount || 0}{" "}
                            {t("specification-lower")}
                          </p>
                        ) : null}
                      </div>
                    </div>
                  </button>
                  {folderItem.hasChildItems && (
                    <button
                      onClick={(e) => toggleExpand(folderItem.id, e)}
                      className="flex py-[8px] rounded-[4px] items-center justify-center w-[32px] h-[40px] shrink-0 transition-colors mr-[6px]"
                      disabled={isLoadingNested}
                    >
                      {isLoadingNested ? (
                        <Loader2 className="h-4 w-4 animate-spin text-[#71717a]" />
                      ) : (
                        <ChevronDown
                          className={`h-4 w-4 text-[#0a0a0a] transition-transform ${
                            isNestedExpanded ? "rotate-180" : ""
                          }`}
                        />
                      )}
                    </button>
                  )}
                </div>
                {isNestedExpanded &&
                  renderNestedItems(folderItem.id, level + 1)}
              </div>
            );
          } else {
            const specItem = item as ISpecificationItem;
            const isNestedSelected = value === specItem.id;

            return (
              <button
                key={specItem.id}
                onClick={() => {
                  onValueChange(specItem.id, "specification");
                  setDropdownOpen(false);
                }}
                className={`w-full flex pl-[8px] py-[4px] items-center gap-2 h-[45px] cursor-pointer relative  rounded-[4px] transition-colors ${
                  isNestedSelected ? "bg-[#F4F4F5]" : "hover:bg-gray-50"
                }`}
                style={{
                  marginLeft: `${paddingLeft}px`,
                  width: `calc(100% - ${paddingLeft}px)`,
                  paddingRight: "55px",
                }}
              >
                {isNestedSelected && (
                  <div className="absolute left-0 top-[13px] w-[2px] h-[20px] bg-[#09090b] rounded-r-full" />
                )}
                <File className="h-4 w-4 text-[#09090b] shrink-0" />
                <p className="text-[12px] font-regular text-[#09090b] text-left leading-4 truncate flex-1">
                  {specItem.name}
                </p>
              </button>
            );
          }
        })}
      </div>
    );
  };

  return (
    <Popover open={dropdownOpen} onOpenChange={handleOpenChange} modal={true}>
      <PopoverTrigger asChild>
        <button
          className={cn(
            "h-[40px] w-auto min-w-[100px] border border-[#e4e4e7] bg-white pl-[6px] pr-[6px] gap-[8px] rounded-md flex items-center justify-between  transition-colors",
            {
              [className]: true,
            }
          )}
        >
          <div className="flex items-center gap-[8px] flex-1 min-w-0">
            <div className="flex items-center justify-center w-[30px] h-[30px] rounded-[6px] bg-[rgba(228,228,231,0.5)] shrink-0">
              {getSelectedIcon()}
            </div>
            <div className="flex flex-col items-start justify-center flex-1 min-w-0">
              {getSelectedLabel() && (
                <p className="text-[9px] mb-[1px] text-left font-500 text-[#71717a] uppercase leading-4 w-full">
                  {getSelectedLabel()}
                </p>
              )}
              <span
                className={cn(
                  "text-[14px]  text-left !font-600 text-[#09090b] leading-4 w-full",
                  labelClassName
                )}
              >
                {getDisplayText()}
              </span>
            </div>
          </div>
          <ChevronDown className="h-4 w-4 text-[#09090b] shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[400px] p-0 overflow-hidden border border-[#e4e4e7] shadow-[0px_4px_6px_-1px_rgba(0,0,0,0.1),0px_2px_4px_-2px_rgba(0,0,0,0.1)] rounded-lg bg-white z-[1000]"
        align={align}
      >
        {/* Search Input */}
        <div className="flex items-center border-b border-[#e4e4e7] px-3 py-[10px]">
          <Search className="h-4 w-4 text-[#71717a] mr-2 shrink-0" />
          <Input
            placeholder={t("search-project-or-specification")}
            value={searchValue}
            onChange={handleSearchChange}
            className="flex h-5 w-full bg-transparent text-sm outline-none placeholder:text-[#71717a] border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
          />
        </div>

        {/* Scrollable Content */}
        <div
          ref={scrollContainerRef}
          className="max-h-[273px] max-w-[400px] overflow-y-auto overflow-x-hidden"
        >
          {loading && items.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-[#71717a]" />
            </div>
          ) : error ? (
            <div className="flex items-center justify-center py-8">
              <p className="text-sm text-red-500">{error}</p>
            </div>
          ) : items.length === 0 && !loading ? (
            <div className="flex items-center justify-center py-8">
              <p className="text-sm text-[#71717a]">{t("no-results-found")}</p>
            </div>
          ) : (
            <div className="p-1 max-w-[400px] overflow-x-hidden">
              {/* Tüm Projeler - Selected */}
              {!searchValue && (
                <button
                  onClick={() => {
                    onValueChange("all", "all");
                    setDropdownOpen(false);
                  }}
                  className={`w-full flex rounded-[4px]  items-center gap-2 h-[45px] px-2 py-1.5 cursor-pointer  transition-colors relative ${
                    isAllSelected ? "bg-[#F4F4F5]" : "hover:bg-gray-50"
                  }`}
                >
                  <div className="flex items-center justify-center w-[30px] h-[30px] rounded-[6px] bg-[rgba(228,228,231,0.5)] shrink-0">
                    <Folders className="h-4 w-4 text-[#09090b]" />
                  </div>
                  <div className="flex flex-col items-start justify-center flex-1 min-w-0">
                    <p className="text-[12px] font-regular text-[#09090b] text-left leading-4 truncate w-full">
                      {t("all-projects")}
                    </p>
                    <p className="text-[10px] text-left font-book text-[#71717a] leading-4 truncate w-full">
                      {pagination.totalCount > 0
                        ? `${folderCount} ${t("project")}, ${specCount} ${t(
                            "specification"
                          )}`
                        : t("loading")}
                    </p>
                  </div>
                  {isAllSelected && (
                    <div className="absolute left-0 top-[13px] w-[2px] h-[20px] bg-[#09090b] rounded-r-full" />
                  )}
                </button>
              )}

              {/* API'den gelen itemler */}
              {items.map((item) => {
                if (item.itemType === "folder") {
                  const folderItem = item as IFolderItem;
                  const isExpanded = expandedItems.has(folderItem.id);
                  const folderContent = nestedFolderContents[folderItem.id];
                  const isLoadingNested = folderContent?.isLoading || false;

                  const isSelected = value === folderItem.id;

                  return (
                    <div key={folderItem.id}>
                      <div
                        className={`flex items-center h-[45px]  relative rounded-[4px]  ${
                          isSelected ? "bg-[#F4F4F5]" : "hover:bg-gray-50"
                        }`}
                      >
                        {isSelected && (
                          <div className="absolute left-0 top-[13px] w-[2px] h-[20px] bg-[#09090b] rounded-r-full" />
                        )}
                        <button
                          onClick={() => {
                            onValueChange(folderItem.id, "folder");
                            setDropdownOpen(false);
                          }}
                          className="flex py-[4px] items-center gap-2 flex-1 min-w-0 px-2 cursor-pointer"
                        >
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <div className="flex items-center justify-center w-[30px] h-[30px] rounded-[6px] bg-[rgba(228,228,231,0.5)] shrink-0">
                              <Folder className="h-4 w-4 text-[#09090b]" />
                            </div>
                            <div className="flex flex-col items-start justify-center flex-1 min-w-0">
                              <p className="text-[12px] font-regular text-[#09090b] leading-4 truncate w-full text-left">
                                {folderItem.name}
                              </p>
                              {(folderItem as any).folderCount !== undefined ||
                              (folderItem as any).totalSpecificationCount !==
                                undefined ? (
                                <p className="text-[10px] text-left font-book text-[#71717a] leading-4 truncate w-full">
                                  {(folderItem as any).folderCount || 0}{" "}
                                  {t("project")},{" "}
                                  {(folderItem as any)
                                    .totalSpecificationCount || 0}{" "}
                                  {t("specification-lower")}
                                </p>
                              ) : null}
                            </div>
                          </div>
                        </button>
                        {folderItem.hasChildItems && (
                          <button
                            onClick={(e) => toggleExpand(folderItem.id, e)}
                            className="flex py-[8px] rounded-[4px] items-center justify-center w-[32px] h-[40px] shrink-0 transition-colors mr-[6px]"
                            disabled={isLoadingNested}
                          >
                            {isLoadingNested ? (
                              <Loader2 className="h-4 w-4 animate-spin text-[#71717a]" />
                            ) : (
                              <ChevronDown
                                className={`h-4 w-4 text-[#0a0a0a] transition-transform ${
                                  isExpanded ? "rotate-180" : ""
                                }`}
                              />
                            )}
                          </button>
                        )}
                      </div>
                      {isExpanded &&
                        folderContent &&
                        renderNestedItems(folderItem.id, 1)}
                    </div>
                  );
                } else {
                  const specItem = item as ISpecificationItem;
                  const isSelected = value === specItem.id;

                  return (
                    <button
                      key={specItem.id}
                      onClick={() => {
                        onValueChange(specItem.id, "specification");
                        setDropdownOpen(false);
                      }}
                      className={`w-full flex py-[4px] items-center gap-2 h-[45px] px-2 cursor-pointer relative border-b border-[#e4e4e7] transition-colors ${
                        isSelected ? "bg-[#F4F4F5]" : "hover:bg-gray-50"
                      }`}
                    >
                      {isSelected && (
                        <div className="absolute left-0 top-[13px] w-[2px] h-[20px] bg-[#09090b] rounded-r-full" />
                      )}
                      <File className="h-4 w-4 text-[#09090b] shrink-0" />
                      <p className="text-[12px] font-regular text-[#09090b] text-left leading-4 truncate flex-1">
                        {specItem.name}
                      </p>
                    </button>
                  );
                }
              })}

              {/* Loading more indicator */}
              {loadingMore && (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-4 w-4 animate-spin text-[#71717a]" />
                </div>
              )}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
